import { z } from 'zod';

/**
 * Provenance label for `metadata.dataStatus`.
 *
 * - `verified`    — geometry / population / etc. confirmed against an authoritative source
 *                    (GASTAT census, geoBoundaries ADM2, etc.).
 * - `unverified`  — Excel row whose boundary came from a low-precision source (OSM locality
 *                    / synthetic placeholder box), or where Google couldn't resolve the
 *                    `customCode` at import time. Listed for review.
 */
export const regionMetadataDataStatusSchema = z.enum(['verified', 'unverified']);
export type RegionMetadataDataStatus = z.infer<typeof regionMetadataDataStatusSchema>;

/**
 * Optional audit / provenance / type-specific block attached to a Region.
 *
 * Common fields (any type):
 * - `population`, `populationYear`, `areaKm2`
 * - `dataStatus`, `boundarySource`, `boundaryPrecision`
 *
 * City-specific (filled from the Cities sheet of the Saudi master import):
 * - `bayanName`           — Saudi Arabia's official BAYAN logistics-platform city name (e.g. `AL-DWADMY`)
 * - `bayanCityId`         — BAYAN's numeric city id, required on every government waybill
 * - `isMajorLogisticsHub` — Excel-flagged primary logistics city (capitals, ports, industrial cities)
 *
 * Every field is optional. New region docs created by the Google-auto flow may carry no metadata at all.
 */
export const regionMetadataSchema = z.object({
  // --- common ---
  /** Resident population (latest known year). */
  population: z.number().optional(),
  /** Year `population` was sampled. */
  populationYear: z.number().optional(),
  /** Total area in square kilometres. */
  areaKm2: z.number().optional(),
  /** Provenance flag — `verified` or `unverified` (see `regionMetadataDataStatusSchema`). */
  dataStatus: regionMetadataDataStatusSchema.optional(),
  /**
   * Where the boundary geometry came from. Common values from the Saudi master Excel:
   * `geoBoundaries_governorate`, `geoBoundaries_ADM2`, `osm_town`, `osm_locality`, `GASTAT_2022`,
   * `known_industrial_city_extent`, `web_search_derived`, `placeholder_box`, ...
   */
  boundarySource: z.string().trim().optional(),
  /**
   * How precise the boundary is. Common values:
   * `exact_polygon`, `governorate_polygon`, `province_aggregated`, `approx_box`,
   * `osm_center_approx_box`, `placeholder_box`, ...
   */
  boundaryPrecision: z.string().trim().optional(),

  // --- city-specific (filled from the Cities sheet) ---
  /**
   * BAYAN — Saudi Arabia's official logistics-platform — name for this city.
   * Typically all-uppercase with hyphens (e.g. `AL-DWADMY`). Used to cross-reference
   * Hero shipments against BAYAN waybill records.
   */
  bayanName: z.string().trim().optional(),
  /**
   * BAYAN's numeric `cityId` for this city (appendix 6.4 of the Bayan
   * integration guide, e.g. `3` for `AL-DWADMY`).
   *
   * Bayan requires `cityId` — not the name — on every waybill location whose
   * `countryCode` is `SA`, so this is what actually goes on the wire.
   * {@link bayanName} stays as the human-readable cross-reference and as the
   * key the seed migration matches on.
   *
   * Absent for non-city region types and for cities Bayan doesn't list. An
   * absent value blocks government-waybill issuance for that leg with an
   * `city_unmapped` error rather than guessing a nearby city.
   */
  bayanCityId: z.number().int().positive().optional(),
  /**
   * `true` if the Excel marks this city as a primary logistics city (capitals, ports,
   * industrial cities). Drives prioritized rendering in dispatcher pickers and
   * the static-bundle inclusion rule for `@hero/lang` region translations.
   */
  isMajorLogisticsHub: z.boolean().optional(),

  // --- center provenance (Google-only strategy at import time) ---
  /**
   * How `center.coordinates` was decided at import time.
   * - `google` — Places + reverse-geocode (cities / hubs).
   * - `excel`  — lat/lng + `geometry` from the Saudi master workbook
   *              (governorates / states, whose Excel polygons are real
   *              admin boundaries; city Excel lat/lng is still untrusted).
   */
  centerSource: z.enum(['google', 'excel']).optional(),
  /** Exact Text Search query sent to Google (e.g. "Jeddah, Saudi Arabia"). */
  centerGoogleQuery: z.string().trim().optional(),
});
export type RegionMetadata = z.infer<typeof regionMetadataSchema>;
